import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contactsApi } from "@/api";
import { useLang } from "@/i18n";
import { ContactTypeBadge } from "@/components/Badges";
import { ContactDialog } from "@/components/ContactDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function Contacts() {
  const { t } = useLang();
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    contactsApi.list({ q: q || undefined, type }).then(setContacts).catch(() => toast.error(t("error")));
  }, [q, type, t]);

  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [load]);

  const remove = async () => {
    try {
      await contactsApi.remove(deleting.id);
      toast.success(t("contact_deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.status === 400 ? t("contact_has_docs") : t("error"));
    } finally { setDeleting(null); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex gap-2 flex-1 max-w-xl">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="contacts-search-input" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44" data-testid="contacts-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_types")}</SelectItem>
              <SelectItem value="customer">{t("customer")}</SelectItem>
              <SelectItem value="supplier">{t("supplier")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-800 hover:bg-blue-700" data-testid="create-contact-button">
          <Plus size={16} className="mr-1" /> {t("new_contact")}
        </Button>
      </div>

      <div className="erp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3">{t("name")}</th>
                <th className="px-4 py-3">{t("type")}</th>
                <th className="px-4 py-3">{t("vat_id")}</th>
                <th className="px-4 py-3 hidden lg:table-cell">{t("tax_office")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("city")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("phone")}</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {contacts.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/contacts/${c.id}`} className="font-medium text-blue-800 dark:text-blue-300 hover:underline" data-testid={`contact-name-${c.id}`}>{c.name}</Link>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </td>
                  <td className="px-4 py-3"><ContactTypeBadge type={c.type} /></td>
                  <td className="px-4 py-3 font-mono">{c.afm || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-600 dark:text-slate-300">{c.doy || "—"}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600 dark:text-slate-300">{c.city || "—"}</td>
                  <td className="px-4 py-3 hidden md:table-cell font-mono text-slate-600 dark:text-slate-300">{c.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Link to={`/contacts/${c.id}`} title={t("contact_card")} data-testid={`contact-docs-${c.id}`}
                        className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500">
                        <FileText size={15} />
                      </Link>
                      <button onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`contact-edit-${c.id}`}
                        className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => setDeleting(c)} data-testid={`contact-delete-${c.id}`}
                        className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="contacts-empty">{t("no_results")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ContactDialog open={dialogOpen} onOpenChange={setDialogOpen} contact={editing} onSaved={load} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_contact")} onConfirm={remove} />
    </div>
  );
}
